import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import { useSeo } from "@/hooks/useSeo";
import { api } from "@/lib/api";
import { Lightbulb, Globe2, ArrowLeft, Loader2 } from "lucide-react";
import { motion } from "framer-motion";

const CATS = ["all", "tips", "destinations"];

export default function Tips() {
  const { t, lang } = useApp();
  const tp = t.tips;
  useSeo({ title: tp.title, description: tp.sub });
  const [params, setParams] = useSearchParams();
  const active = params.get("cat") || "all";
  const [items, setItems] = useState(null);

  useEffect(() => {
    setItems(null);
    api.get(`/articles${active !== "all" ? `?category=${active}` : ""}`).then((r) => setItems(r.data)).catch(() => setItems([]));
  }, [active]);

  return (
    <div className="bg-sand min-h-screen" data-testid="tips-page">
      <section className="bg-tigris text-white px-4 sm:px-6 pt-12 pb-16">
        <div className="max-w-6xl mx-auto">
          <h1 className="text-4xl sm:text-5xl font-bold font-display mb-3">{tp.title}</h1>
          <p className="text-white/80 max-w-2xl">{tp.sub}</p>
        </div>
      </section>
      <section className="max-w-6xl mx-auto px-4 sm:px-6 -mt-6 pb-20">
        <div className="flex gap-2 mb-8 flex-wrap">
          {CATS.map((c) => (
            <button key={c} data-testid={`tips-cat-${c}`} onClick={() => setParams(c === "all" ? {} : { cat: c })}
              className={`px-4 py-2 rounded-full text-sm font-bold shadow-sm transition-colors flex items-center gap-1.5 ${active === c ? "bg-gold text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}>
              {c === "tips" && <Lightbulb size={14} />}{c === "destinations" && <Globe2 size={14} />}{tp.cats[c]}
            </button>
          ))}
        </div>
        {items === null ? <div className="flex justify-center py-20"><Loader2 className="animate-spin text-tigris" size={36} /></div> : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {items.map((a, i) => (
              <motion.article key={a.id} initial={{ opacity: 0, y: 18 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.06 }}
                className="bg-white rounded-2xl overflow-hidden border border-slate-100 shadow-sm card-hover flex flex-col">
                <Link to={`/tips/${a.id}`} data-testid={`article-${a.id}`} className="block h-48 overflow-hidden">
                  <img src={a.image} alt={a[`title_${lang}`]} loading="lazy" className="w-full h-full object-cover hover:scale-105 transition-transform duration-500" />
                </Link>
                <div className="p-5 flex flex-col flex-1">
                  <span className={`self-start text-[11px] font-bold px-2.5 py-1 rounded-full mb-3 ${a.category === "tips" ? "bg-gold/15 text-gold" : "bg-tigris/10 text-tigris"}`}>{tp.cats[a.category]}</span>
                  <h2 className="font-bold text-slate-800 text-base md:text-lg mb-2 leading-snug">{a[`title_${lang}`]}</h2>
                  <p className="text-sm text-slate-500 leading-relaxed flex-1">{a[`excerpt_${lang}`]}</p>
                  <Link to={`/tips/${a.id}`} className="mt-4 inline-flex items-center gap-1.5 text-sm font-bold text-tigris hover:gap-2.5 transition-all">{tp.readMore} <ArrowLeft size={15} className="ltr:rotate-180" /></Link>
                </div>
              </motion.article>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
